import { FC, useState, useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getReadinessBadgeColor, getReadinessColor } from "../../../utils/readinessHelpers";
import { ITeam } from "../../../models/Team";
import CustomTable from "../../../shared/CustomTable/CustomTable";
import { renderNameUser } from "../../../utils/table";
import { useEndpoints } from "../../../hooks/useEndpoints";
import dayjs from "dayjs";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import CustomDatePicker from "../../../shared/CustomDatePicker/CustomDatePicker";
import { InjuryBadge } from "../../../shared/InjuryBadge/InjuryBadge";
import { CertificateBadge } from "../../../shared/CertificateBadge/CertificateBadge";
import { useInjuryEndpoints } from "../../../hooks/useInjuryEndpoints";
import { IActiveInjurySummary } from "../../../models/injury";
import TeamInvitesModal from "../TeamInvites/TeamInvitesModal";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import { UserPlus } from "lucide-react";
import { rpeDaIndiceSforzo } from "@leopart/calc";
import styles from "./AthletesTab.module.scss";

const renderStatCell = (values: [number | null, number | null] | null) => {
  // values = [current (periodo attuale), previous (periodo precedente)]
  if (!values || values[0] === null) return <span>—</span>;

  const current = values[0];
  const previous = values[1];
  const diff = previous !== null ? current - previous : null;

  // Se recovery = 0 non mostrare variazioni percentuali
  const showDelta = current > 0 && diff !== null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span>{current.toFixed(1)}%</span>
      {showDelta && (
        <CustomBadge
          color={diff! >= 0 ? "lightgreen" : "lightred"}
          label={`${diff! >= 0 ? "▲" : "▼"} ${Math.abs(diff!).toFixed(1)}%`}
        />
      )}
    </div>
  );
};

// Sforzo su scala 1-10 (il backend restituisce linearInterpolation 0-100, reverse map → 1-10)
const renderEffortCell = (values: [number | null, number | null] | null) => {
  if (!values || values[0] === null) return <span>—</span>;

  // La conversione indice→RPE vive in @leopart/calc: stessa formula per web,
  // mobile e test del backend. Qui c'era una copia scritta a mano che trattava
  // l'indice 0 come "nessun dato" — ma 0 è RPE 1, cioè l'atleta che si è
  // allenato pochissimo, proprio quello che si vuole vedere. L'assenza vera il
  // backend la manda come `null`.
  const current = rpeDaIndiceSforzo(values[0]);
  const previous = rpeDaIndiceSforzo(values[1]);
  if (current === null) return <span>—</span>;
  const diff = previous !== null ? Math.round(current) - Math.round(previous) : null;
  const showDelta = diff !== null && diff !== 0;

  const rpeColor = current >= 8 ? '#f57c73' : current >= 6 ? '#FFD15A' : '#27ae60';

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontWeight: 600, color: rpeColor }}>{Math.round(current)}</span>
      {showDelta && (
        <CustomBadge
          color={diff! <= 0 ? "lightgreen" : "lightred"}
          label={`${diff! <= 0 ? "▼" : "▲"} ${Math.abs(diff!)}`}
        />
      )}
    </div>
  );
};

const AthletesTab: FC<{ teamQuery: UseQueryResult<ITeam, Error> }> = ({
  teamQuery,
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const endpoints = useEndpoints();
  const [startDate, setStartDate] = useState<Date>(dayjs().subtract(7, 'day').toDate());
  const [endDate, setEndDate] = useState<Date>(dayjs().toDate());
  /** La creazione dei link è un'azione occasionale: vive in una modal. */
  const [invitesOpen, setInvitesOpen] = useState(false);

  const today = useMemo(() => new Date(), []);

  const startDateStr = useMemo(() => dayjs(startDate).format("YYYY-MM-DD"), [startDate]);
  const endDateStr = useMemo(() => dayjs(endDate).format("YYYY-MM-DD"), [endDate]);

  const period = useMemo(() => {
    const diff = dayjs(endDate).diff(dayjs(startDate), "day");
    return diff < 1 ? 1 : diff;
  }, [startDate, endDate]);

  const injuryEndpoints = useInjuryEndpoints();

  const statsQuery = useQuery({
    queryKey: ["team-stats", params.id, startDateStr, endDateStr],
    queryFn: async () => {
      const { data } = await endpoints.getTeamStats(params.id!, { startDate: startDateStr, endDate: endDateStr });
      return data;
    },
    enabled: !!params.id,
  });

  const injuriesQuery = useQuery<IActiveInjurySummary[]>({
    queryKey: ["team-injuries", params.id],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getTeamInjuries(params.id!);
      return data;
    },
    enabled: !!params.id,
    staleTime: 1000 * 60 * 5,
  });

  const injuredMap = useMemo(() => {
    const map: Record<string, IActiveInjurySummary> = {};
    (injuriesQuery.data ?? []).forEach((inj) => { map[inj.athleteId] = inj; });
    return map;
  }, [injuriesQuery.data]);

  // Soglie RI dal canone @leopart/calc (70/50/30) via readinessHelpers —
  // niente più 75/60 locali che divergevano da mobile e dalle altre viste.
  const getRiColor = (ri: number) => getReadinessBadgeColor(getReadinessColor(ri));

  const headers = ["Nome", "RI recente", "Stato", "Recovery", "RPE (oggi)", "Ultima attività"];

  const fields = {
    Nome: {
      key: "firstName",
      customComponent: (value: any, record: any) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/dashboard/users/${record._id}`)}
        >
          {renderNameUser(value, record)}
        </div>
      ),
    },
    "RI recente": {
      key: "_id",
      customComponent: (_: any, record: any) => {
        // recovery[0] è il valore più recente di totalRecoveries.interpolation (0-100)
        const ri: number | null = record.recovery?.[0] ?? null;
        if (ri == null) return <span style={{ color: 'var(--c-text-3)', fontSize: 13 }}>—</span>;
        return (
          <span style={{ fontWeight: 600, fontSize: 14, color: getRiColor(ri) }}>
            {ri.toFixed(0)}
          </span>
        );
      },
    },
    Stato: {
      key: "_id",
      customComponent: (_: any, record: any) => {
        const inj = injuredMap[record._id];
        /**
         * "Disponibile" richiede un dato di recupero.
         *
         * Prima bastava non essere infortunati: 24 atleti iscritti da un
         * giorno, senza una sola compilazione, risultavano tutti disponibili —
         * mentre `GET /teams/pulse`, sullo stesso team, rispondeva
         * `available: 0, noResponse: 24`. Di chi non compila non sappiamo il
         * rischio, e "non lo so" non si arrotonda a "sta bene".
         */
        const haDatoRecupero =
          typeof record.recovery?.[0] === "number";
        // TODO: medicalCertificateExpiry sarà disponibile dopo deploy backend phase7
        // (l'endpoint /teams/:id/stats non restituisce ancora questo campo)
        return (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            {inj && <InjuryBadge size="sm" injury={inj} />}
            {record.medicalCertificateExpiry && (
              <CertificateBadge
                size="sm"
                certificate={{ medicalCertificateExpiry: record.medicalCertificateExpiry }}
              />
            )}
            {!inj && !record.medicalCertificateExpiry && (
              <CustomBadge
                label={haDatoRecupero ? "Disponibile" : "Senza risposta"}
                color={haDatoRecupero ? "lightgreen" : "light-blue-dust"}
              />
            )}
          </div>
        );
      },
    },
    Recovery: {
      key: "recovery",
      customComponent: (_: any, record: any) =>
        renderStatCell(record.recovery ?? null),
    },
    // La chiave DEVE combaciare con l'header: `CustomTable` cerca il campo per
    // nome di colonna. Si chiamava "Sforzo" mentre l'header dice "RPE (oggi)",
    // quindi la colonna restava vuota per tutti — nessun errore, solo niente.
    "RPE (oggi)": {
      key: "effortIndex",
      customComponent: (_: any, record: any) =>
        renderEffortCell(record.effortIndex ?? null),
    },
    "Ultima attività": {
      key: "lastActivityDate",
      customValue: (value: any) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "—",
    },
  };

  return (
    <>
      {/* Periodo a sinistra, azione a destra: una riga sola sopra la tabella. */}
      <div className={styles.toolbar}>
        <div className={styles.period}>
          <span className={styles.periodLabel}>Dal</span>
          <CustomDatePicker
            date={startDate}
            onChange={(d) => d && setStartDate(d)}
            maxDate={endDate}
            isClearable={false}
            showIcon={true}
            placeholder="Data inizio"
          />
          <span className={styles.periodLabel}>al</span>
          <CustomDatePicker
            date={endDate}
            onChange={(d) => d && setEndDate(d)}
            minDate={startDate}
            maxDate={today}
            isClearable={false}
            showIcon={true}
            placeholder="Data fine"
          />
          <span className={styles.periodCount}>
            ({period} giorn{period === 1 ? "o" : "i"})
          </span>
        </div>

        {/* L'icona va nella prop dedicata: passata come figlio, CustomButton la
            impila sopra il testo invece di metterla in riga. */}
        <CustomButton
          color="secondary"
          size="sm"
          icon={<UserPlus size={15} strokeWidth={2.2} />}
          iconPosition="left"
          onClick={() => setInvitesOpen(true)}
        >
          Invita atleti
        </CustomButton>
      </div>

      <TeamInvitesModal
        teamId={params.id ?? ""}
        isOpen={invitesOpen}
        onClose={() => setInvitesOpen(false)}
      />

      <CustomTable
        tableLayout="auto"
        headers={headers}
        data={statsQuery.data ?? []}
        fields={fields}
        isLoading={teamQuery.isPending || statsQuery.isPending}
      />
    </>
  );
};

export default AthletesTab;
